import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarClock, X, ArrowRight } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { cn } from "@/lib/utils";

const DAY = 86400000;
const ACK_KEY = "fcs_renew_ack";

function todayISO(ms) {
  return new Date(ms).toISOString().slice(0, 10);
}

// Gentle in-app nudge shown when the premium subscription has <= 3 days left,
// so users can renew before access lapses. Dismissible per day (localStorage).
export function RenewReminderBanner({ className }) {
  const navigate = useNavigate();
  const { premium, subscriptionUntil, now } = useApp();
  const today = todayISO(now);
  const [acked, setAcked] = useState(() => {
    try {
      return localStorage.getItem(ACK_KEY) === today;
    } catch {
      return false;
    }
  });

  if (!premium || !subscriptionUntil) return null;
  const daysLeft = Math.ceil((subscriptionUntil - now) / DAY);
  if (daysLeft < 0 || daysLeft > 3) return null;
  if (acked) return null;

  const when =
    daysLeft <= 0 ? "hari ini" : daysLeft === 1 ? "besok" : `dalam ${daysLeft} hari`;

  const dismiss = () => {
    try {
      localStorage.setItem(ACK_KEY, today);
    } catch {
      /* ignore */
    }
    setAcked(true);
  };

  return (
    <div
      className={cn(
        "rounded-2xl border border-amber-200 bg-amber-50 p-4",
        className
      )}
      data-testid="renew-reminder-banner"
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white shadow-sm">
          <CalendarClock className="h-4 w-4 text-amber-600" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-heading text-sm font-bold text-amber-900" data-testid="renew-reminder-title">
            Langganan berakhir {when}
          </p>
          <p className="mt-1 text-xs leading-relaxed text-amber-700">
            Perpanjang sekarang agar akses fitur premium (ketahanan &amp; analisis
            lengkap) tidak terputus.
          </p>
          <button
            data-testid="renew-reminder-cta"
            onClick={() => navigate("/paywall")}
            className="mt-3 flex h-10 w-full items-center justify-center gap-2 rounded-full bg-amber-600 text-sm font-semibold text-white transition-colors hover:bg-amber-700"
          >
            Perpanjang Langganan <ArrowRight className="h-4 w-4" />
          </button>
        </div>
        <button
          data-testid="renew-reminder-dismiss"
          onClick={dismiss}
          className="shrink-0 text-amber-400 transition-colors hover:text-amber-600"
          aria-label="Tutup pengingat"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
